import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { map } from 'lodash'
import { useTranslation } from 'react-i18next'
import { TariffType } from '../../../../swagger/dto'

const useMapTariff = () => {
    const { t } = useTranslation()

    return {
        ['FREE']: {
            available: [
                {
                    key: 'available_models',
                    title: t('tariff.freeModels'),
                    description: t('tariff.freeModelsDescription'),
                },
                {
                    title: t('tariff.diskSpace'),
                    description: t('tariff.diskSpaceDescription'),
                },
            ],
            unavailable: [
                {
                    title: t('tariff.assistants'),
                    description: t('tariff.assistantsDescription'),
                },
                {
                    title: t('tariff.meetingRecording'),
                    description: t('tariff.meetingRecordingDescription'),
                },
                {
                    title: t('tariff.topModelsOpenAI'),
                    description: t('tariff.topModelsOpenAIDescription'),
                },
                {
                    title: t('tariff.topModelsGeneration'),
                    description: t('tariff.topModelsGenerationDescription'),
                },
            ],
        },
        [TariffType.START]: {
            available: [
                {
                    key: 'available_models',
                    title: t('tariff.models'),
                    description: t('tariff.modelsDescriptionStart'),
                },
                {
                    key: 'available_assistants',
                    title: t('tariff.assistantsWord'),
                    description: t('tariff.assistantsDescriptionStart'),
                },
                {
                    title: t('tariff.diskSpace'),
                    description: t('tariff.diskSpaceDescription'),
                },
            ],
            unavailable: [
                {
                    title: t('tariff.meetingRecording'),
                    description: t('tariff.meetingRecordingDescription'),
                },
                {
                    title: t('tariff.topModelsOpenAI'),
                    description: t('tariff.topModelsOpenAIDescription'),
                },
                {
                    title: t('tariff.topModelsGeneration'),
                    description: t('tariff.topModelsGenerationDescription'),
                },
            ],
        },
        [TariffType.PROFESSIONAL]: {
            available: [
                {
                    key: 'available_models',
                    title: t('tariff.models'),
                    description: t('tariff.modelsDescriptionPro'),
                },
                {
                    key: 'available_assistants',
                    title: t('tariff.assistantsWord'),
                    description: t('tariff.assistantsDescriptionPro'),
                },
                {
                    title: t('tariff.diskSpace'),
                    description: t('tariff.diskSpaceDescription'),
                },
                {
                    title: t('tariff.meetingRecording'),
                    description: t('tariff.meetingRecordingShortDescription'),
                },
            ],
            unavailable: [],
        },
    }
}

const WrapperDescription = ({ isMobile, tariff_type, restrictions, children }) => {
    const { t } = useTranslation()

    const getShortDescription = () => {
        if (tariff_type === 'FREE') {
            return t('tariff.freeShortDescription')
        }
        if (tariff_type === 'START') {
            return t('tariff.startShortDescription', {
                models: restrictions?.available_models.length,
                assistants: restrictions?.available_assistants.length,
            })
        }
        if (tariff_type === 'PROFESSIONAL') {
            return t('tariff.professionalShortDescription')
        }
        return ''
    }

    if (isMobile) {
        return (
            <>
                <Typography variant="body2" sx={{ color: '#5A5F72', mt: 1, pr: 7 }}>
                    {getShortDescription()}
                </Typography>
                <Accordion sx={{ background: 'none' }}>
                    <AccordionSummary
                        sx={{
                            padding: 0,
                            justifyContent: 'flex-start',
                            flexGrow: 0,
                            '& .MuiAccordionSummary-content': {
                                flexGrow: 0,
                            },
                        }}
                        expandIcon={<ArrowDropDownIcon />}>
                        <Typography variant="h6">{t('tariff.moreDetails')}</Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ width: '100%' }}>{children}</AccordionDetails>
                </Accordion>
            </>
        )
    } else {
        return children
    }
}

export const PackageDescription = ({ isMobile, tariff_type, restrictions }) => {
    const { t } = useTranslation()
    const mapTariff = useMapTariff()

    return (
        <WrapperDescription
            isMobile={isMobile}
            tariff_type={tariff_type}
            restrictions={restrictions}>
            <Typography gutterBottom variant="body2" mt={2}>
                {t('tariff.available')}
            </Typography>
            {map(mapTariff[tariff_type]?.available, (text, key) => {
                const title = text?.key
                    ? `${restrictions?.[text.key].length} ${text.title} `
                    : text.title
                return (
                    <Tooltip key={key} title={text.description}>
                        <Stack direction="row" spacing={1}>
                            <CheckIcon fontSize="small" sx={{ color: '#229F0A', height: '18px' }} />
                            <Typography
                                variant="body2"
                                sx={{
                                    textDecoration: 'underline',
                                    textDecorationStyle: 'dotted',
                                    textDecorationColor: '#5A5C5E'
                                }}>
                                {title}
                            </Typography>
                        </Stack>
                    </Tooltip>
                )
            })}

            {mapTariff[tariff_type]?.unavailable.length > 0 && (
                <>
                    <Typography gutterBottom variant="body2" mt={2}>
                        {t('tariff.unavailable')}
                    </Typography>

                    {map(mapTariff[tariff_type].unavailable, (text, key) => {
                        return (
                            <Tooltip key={key} title={text.description}>
                                <Stack direction="row" spacing={1}>
                                    <CloseIcon
                                        fontSize="small"
                                        sx={{ color: '#5A5C5E', height: '18px' }}
                                    />
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: '#5A5F72',
                                            textDecoration: 'underline',
                                            textDecorationStyle: 'dotted',
                                            textDecorationColor: '#5A5C5E'
                                        }}>
                                        {text.title}
                                    </Typography>
                                </Stack>
                            </Tooltip>
                        )
                    })}
                </>
            )}
        </WrapperDescription>
    )
}
